/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { PaymentOnAccountModel } from "@eisinc/App/Components/PaymentCenter/PaymentOnAccount";
import PDFLinkCell from "@eisinc/App/Components/PaymentCenter/PDFLinkCell";
import {
    calcSelectedInvoicesTotal,
    formatter,
    getPaginatedInvoicePayments,
    handleSortInvoicePayments,
    handleSwitchSortArrow,
} from "@eisinc/App/Utilities/PaymentCenterUtilities";
import Checkbox from "@eisinc/MobiusOverrides/Checkbox/Checkbox";
import Pagination, { PaginationPresentationProps } from "@eisinc/MobiusOverrides/Pagination";
import Tooltip, { TooltipPresentationProps } from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import { DEFAULT_PAGE_SIZE, InvoicePaymentAttributes, SORT_INVOICE_PAYMENT_BY_FIELD } from "@eisinc/XDL/constants";
import { InvoicePaymentModel } from "@eisinc/XDL/interfaces";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import DataTable from "@insite/mobius/DataTable";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import {
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Link,
    Typography,
} from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";

interface OwnProps {
    invoicePaymentItems?: InvoicePaymentModel[];
    setSelectedCredits: (selectedCredits: InvoicePaymentModel[]) => void;
    currentStep: number;
    setCurrentStep: (i: number) => void;
    selectedInvoicePayments: InvoicePaymentModel[] | undefined;
    paymentOnAccountData?: PaymentOnAccountModel;
}

export interface AvailableCreditsTableStyle {
    wrapper?: InjectableCss;
    tableWrapper?: InjectableCss;
    tableHeaderWrapper?: InjectableCss;
    headerLeftWrapper?: InjectableCss;
    invoicesCount?: TypographyProps;
    invoicesLabel?: TypographyProps;
    invoicesTotal?: TypographyProps;
    invoiceItemNumberDataCellLink?: LinkPresentationProps;
    backLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    tooltip?: TooltipPresentationProps;
    tooltipWrapper?: InjectableCss;
    selectedRow?: DataTableRowProps;
}

export const availableCreditsTableStyles: AvailableCreditsTableStyle = {};

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const AvailableCreditsTable: React.FunctionComponent<Props> = ({
    invoicePaymentItems,
    setSelectedCredits,
    currentStep,
    setCurrentStep,
    selectedInvoicePayments,
    paymentOnAccountData,
    language,
}: Props) => {
    const styles = availableCreditsTableStyles;

    const [invoicePayments, setInvoicePayments] = React.useState<InvoicePaymentModel[] | undefined>();
    const [sortOrder, setSortOrder] = React.useState(SORT_INVOICE_PAYMENT_BY_FIELD);
    const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
    const [page, setPage] = React.useState(1);
    const pageSizeOptions = [DEFAULT_PAGE_SIZE];
    const [totalSelectedInvoices, setTotalSelectedInvoices] = React.useState(0);
    const [totalSelectedCredits, setTotalSelectedCredits] = React.useState(0);

    const handleSortInvoicePaymentsEvent = (sortField: keyof InvoicePaymentModel) => {
        const updateSortOrder = handleSwitchSortArrow(sortField, sortOrder);
        setSortOrder(updateSortOrder);
        const sortedInvoicePayments = handleSortInvoicePayments(invoicePayments, sortField, updateSortOrder);
        setInvoicePayments(sortedInvoicePayments);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        if (!newPageSize) {
            setPage(1);
            setPageSize(1000);
        } else {
            setPageSize(newPageSize);
        }
    };

    const paginatedInvoicePayment = React.useMemo(() => {
        return getPaginatedInvoicePayments(page, pageSize, invoicePayments);
    }, [invoicePayments, page, pageSize, sortOrder]);

    const rows = paginatedInvoicePayment?.map(invoice => {
        return {
            id: invoice.invoiceItemNumber,
            invoiceItemNumber: invoice.invoiceItemNumber,
            invoiceNumber: invoice.invoiceNumber,
            po: invoice.purchaseOrderNumber,
            invoiceDate: invoice.invoiceDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.invoiceDate),
                      language,
                  })
                : "",
            sapFiDocumentType: invoice.sapFiDocumentType,
            itemText: invoice.itemText,
            calculatedDueDate: invoice.calculatedDueDate
                ? getLocalizedDateTime({
                      dateTime: new Date(invoice.calculatedDueDate),
                      language,
                  })
                : "",
            invoiceAmount: invoice.invoiceAmount,
            selected: invoice.selected,
            disabled:
                !invoice.selected &&
                (-1 * invoice.invoiceAmount > totalSelectedInvoices ||
                    -1 * invoice.invoiceAmount > totalSelectedInvoices - -1 * totalSelectedCredits),
            isPaid: invoice.hideCheckBox,
            originalInvoiceNumber: invoice.originalInvoiceNumber,
        } as any;
    });

    const selectedInvoices = invoicePayments?.filter(invoice => invoice.selected === true);
    // setSelectedCredits(selectedInvoices || []);

    const selectChangeHandler = (invoiceItemNumber: string, value: boolean) => {
        const newInvoicePayments = [...(invoicePayments as any)];
        const index = newInvoicePayments?.findIndex(invoice => invoice.invoiceItemNumber === invoiceItemNumber);

        newInvoicePayments[index].selected = value;
        setInvoicePayments(newInvoicePayments);
    };

    React.useEffect(() => {
        setSelectedCredits(selectedInvoices || []);
    }, [selectedInvoices]);

    React.useEffect(() => {
        if (invoicePaymentItems) {
            setInvoicePayments(invoicePaymentItems?.filter(invoice => invoice.invoiceAmount < 0));
        }
    }, [invoicePaymentItems]);

    React.useEffect(() => {
        if (currentStep === 1 && selectedInvoices?.length) {
            const newInvoicePayments = [...(invoicePayments as any)];
            newInvoicePayments?.forEach(invoice => {
                invoice.selected = false;
            });
            setInvoicePayments(newInvoicePayments);
        }
    }, [currentStep]);

    React.useEffect(() => {
        let total = calcSelectedInvoicesTotal(selectedInvoicePayments || []);
        if (paymentOnAccountData?.value) {
            total += parseFloat(paymentOnAccountData?.value);
        }
        setTotalSelectedInvoices(total);
    }, [selectedInvoicePayments, paymentOnAccountData]);

    React.useEffect(() => {
        setTotalSelectedCredits(calcSelectedInvoicesTotal(selectedInvoices || []));
    }, [selectedInvoices]);

    const renderTableHeader = () => (
        <StyledWrapper {...styles.tableHeaderWrapper}>
            <StyledWrapper {...styles.headerLeftWrapper}>
                <Typography {...styles.invoicesCount}>{selectedInvoices?.length}</Typography>
                <Typography {...styles.invoicesLabel}>{translate("Credit(s) Selected:")}</Typography>
                <Typography {...styles.invoicesTotal}>{formatter.format(totalSelectedCredits)}</Typography>
            </StyledWrapper>
            <StyledWrapper></StyledWrapper>
        </StyledWrapper>
    );

    if (currentStep !== 2) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Link
                onClick={() => {
                    setCurrentStep(1);
                }}
                {...styles.backLink}
            >
                {translate("Change Selections")}
            </Link>
            {renderTableHeader()}
            <StyledWrapper {...styles.tableWrapper}>
                <DataTable>
                    <DataTableHead>
                        <DataTableHeader
                            sorted={sortOrder.originalInvoiceNumber}
                            onSortClick={() =>
                                handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.originalInvoiceNumber)
                            }
                            data-test-selector="tableHeader_originalInvoiceNumber"
                        >
                            {translate("IPInvoice")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.invoiceItemNumber}
                            onSortClick={() =>
                                handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.invoiceItemNumber)
                            }
                            data-test-selector="tableHeader_invoiceItemNumber"
                        >
                            {translate("IPDOC")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.purchaseOrderNumber}
                            onSortClick={() =>
                                handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.purchaseOrderNumber)
                            }
                            data-test-selector="tableHeader_purchaseOrderNumber"
                        >
                            {translate("PO #")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.invoiceDate}
                            onSortClick={() => handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.invoiceDate)}
                            data-test-selector="tableHeader_invoiceDate"
                        >
                            {translate("Doc. Date")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.calculatedDueDate}
                            onSortClick={() =>
                                handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.calculatedDueDate)
                            }
                            data-test-selector="tableHeader_calculatedDueDate"
                        >
                            {translate("Due Date")}
                        </DataTableHeader>
                        <DataTableHeader
                            sorted={sortOrder.invoiceAmount}
                            onSortClick={() => handleSortInvoicePaymentsEvent(InvoicePaymentAttributes.invoiceAmount)}
                            data-test-selector="tableHeader_invoiceAmount"
                        >
                            {translate("Amount")}
                        </DataTableHeader>
                        <DataTableHeader data-test-selector="tableHeader_invoiceActions">
                            {translate("Actions")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody data-test-selector="invoicePaymentTable_tableBody">
                        {rows?.map(
                            (
                                {
                                    id,
                                    invoiceItemNumber,
                                    po,
                                    invoiceDate,
                                    calculatedDueDate,
                                    invoiceAmount,
                                    selected,
                                    disabled,
                                    itemText,
                                    isPaid,
                                    invoiceNumber,
                                    originalInvoiceNumber,
                                },
                                index,
                            ) => (
                                <React.Fragment key={id}>
                                    <DataTableRow
                                        {...styles.selectedRow}
                                        className={`${selected ? "selected" : ""} ${isPaid ? "paid" : ""}`}
                                    >
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_originalInvoiceNumber">
                                            <StyledWrapper {...styles.tooltipWrapper}>
                                                <PDFLinkCell invoice={rows[index]} showOriginalInvoiceNumber />
                                                {originalInvoiceNumber && (
                                                    <Tooltip {...styles.tooltip} text={itemText} />
                                                )}
                                            </StyledWrapper>
                                        </DataTableCell>
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_invoiceNumber">
                                            {invoiceNumber}
                                        </DataTableCell>
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_purchaseNumber">
                                            {po}
                                        </DataTableCell>
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_invoiceDate">
                                            {invoiceDate}
                                        </DataTableCell>
                                        <DataTableCell data-test-selector="invoicePaymentTable_tableCell_calculatedDueDate">
                                            {calculatedDueDate}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_invoiceAmount"
                                            {...styles.invoiceItemNumberDataCellLink}
                                        >
                                            {formatter.format(invoiceAmount)}
                                        </DataTableCell>
                                        <DataTableCell
                                            data-test-selector="invoicePaymentTable_tableCell_invoiceActions"
                                            className={isPaid ? "pending" : "actions"}
                                        >
                                            {isPaid ? (
                                                <Typography as="span">{translate("Pending")}</Typography>
                                            ) : (
                                                <Checkbox
                                                    uid={id}
                                                    onChange={(e: any, value: boolean) => {
                                                        selectChangeHandler(invoiceItemNumber, value);
                                                    }}
                                                    checked={selected}
                                                    disabled={disabled}
                                                >
                                                    {translate("Apply Credit")}
                                                </Checkbox>
                                            )}
                                        </DataTableCell>
                                    </DataTableRow>
                                </React.Fragment>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            <StyledWrapper>
                <Pagination
                    resultsCount={invoicePayments?.length || 0}
                    currentPage={page}
                    resultsPerPage={pageSize}
                    resultsPerPageLabel={translate("Per Page")}
                    resultsPerPageOptions={pageSizeOptions}
                    onChangeResultsPerPage={changeResultsPerPage}
                    onChangePage={(newPageIndex: number) => setPage(newPageIndex || 1)}
                    withAllOption
                    {...styles.pagination}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(AvailableCreditsTable);
